<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {computed, ref, watch} from "vue";
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";

const props = defineProps({
  accountsJson: {
    type: String,
    required: true
  },
  email: {
    type: String,
    required: true,
  },
  websiteUrl: {
    type: String,
    required: true,
  },
  connectedSelectionJson: {
    type: String,
    default: 'null',
  },
});

import {errorToast, successToast} from "@/composables/useToasts";
import StepBox from "@/components/google/partials/StepBox.vue";
import RadioBox from "@/components/google/partials/RadioBox.vue";
import RadioBoxGrid from "@/components/google/partials/RadioBoxGrid.vue";
import PlaceholderRadioBox from "@/components/google/partials/PlaceholderRadioBox.vue";
import AlreadyConnectedTag from "@/components/google/partials/AlreadyConnectedTag.vue";

const selectedAccountId = ref();
const accounts = computed(() => JSON.parse(props.accountsJson));
const connectedSelection = computed(() => JSON.parse(props.connectedSelectionJson));
const selectedPropertyId = ref();

watch(accounts, () => {
  if (selectedAccountId.value) {
    return;
  }

  const connectedAccountId = connectedSelection.value?.accountId;
  if (connectedAccountId) {
    const connectedAccount = accounts.value.find((account) => account.id === connectedAccountId);
    if (connectedAccount) {
      selectedAccountId.value = connectedAccount.id;
      return;
    }
  }

  if (accounts.value.length === 1) {
    selectedAccountId.value = accounts.value[0].id;
  }
}, {
  immediate: true,
});

watch(
  selectedAccountId,
  () => {
    selectedPropertyId.value = undefined;
  },
  {
    immediate: true,
  },
)

const selectedAccount = computed(() => accounts.value.find(account => account.id === selectedAccountId.value));

const accountProperties = computed(() => selectedAccount.value && selectedAccount.value.propertySummaries ? selectedAccount.value.propertySummaries : []);

watch(accountProperties, () => {
  if (selectedPropertyId.value) {
    return;
  }

  const connectedPropertyId = connectedSelection.value?.propertyId;
  if (
    connectedPropertyId
    && selectedAccountId.value === connectedSelection.value?.accountId
  ) {
    const connectedProperty = accountProperties.value.find((property) => property.id === connectedPropertyId);
    if (connectedProperty) {
      selectedPropertyId.value = connectedProperty.id;
      return;
    }
  }

  if (accountProperties.value.length === 1) {
    selectedPropertyId.value = accountProperties.value[0].id;
  }
}, {
  immediate: true,
});

const selectedProperty = computed(() => {
  if (!selectedAccount.value) {
    return undefined;
  }

  return selectedAccount.value.propertySummaries.find(property => property.id === selectedPropertyId.value)
});

const {mutate: submit, isPending, isSuccess} = useMutation({
  mutationFn: () => axios.post('/ajax/v2/google/analytics', {
    accountId: selectedAccountId.value,
    propertyId: selectedProperty.value.id,
    gaVersion: selectedProperty.value.gaVersion,
  }),
  onSuccess: ({data}) => {
    successToast('Google analytics account connected successfully, redirecting...');
    window.setTimeout(() => {
      window.location.href = data.redirect;
    }, 2000)

  },
  onError: (error) => {
    console.error(error);
    errorToast('Something went wrong, please try again');
  },
})

</script>

<template>
  <div class="tw-flex tw-flex-col tw-gap-y-8 tw-px-4 md:tw-px-0">
    <div>
      <h1 class="tw-font-bold tw-text-3xl md:tw-text-4.5xl">
        Connect Google Analytics
      </h1>
      <p class="tw-text-lg">
        Signed in as <span class="tw-font-bold">{{ email }}</span>, please choose what to link to <span
          class="tw-font-bold"
        >{{ websiteUrl }}</span>
      </p>
    </div>
    <StepBox
      :step="1"
      title="Choose account"
      :active="!selectedAccountId"
    >
      <template #subtitle>
        <p>
          Choose which Google Analytics account you would like to connect:
        </p>
      </template>

      <template #default>
        <RadioBoxGrid>
          <RadioBox
            v-for="account in accounts"
            :key="account.id"
            v-model="selectedAccountId"
            :title="account.displayName"
            :value="account.id"
            :already-connected="connectedSelection?.accountId === account.id"
          >
            <p class="tw-text-neutral-700">
              Account ID: {{ account.id }}
            </p>
          </RadioBox>
        </RadioBoxGrid>
      </template>
    </StepBox>

    <StepBox
      :step="2"
      title="Select property"
      :active="!!selectedAccountId"
    >
      <template #subtitle>
        <p>
          Choose which property to link to your website:
        </p>
      </template>

      <template #default>
        <RadioBoxGrid v-show="accountProperties.length > 0">
          <RadioBox
            v-for="property in accountProperties"
            :key="property.id"
            v-model="selectedPropertyId"
            :title="property.displayName"
            :value="property.id"
          >
            <div class="tw-flex tw-flex-col tw-gap-2">
              <p class="tw-text-neutral-700">
                Property ID: {{ property.id }}
              </p>
              <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
                <span
                  v-if="property.gaVersion"
                  class="tw-px-4 tw-py-2 tw-bg-success-100 tw-border tw-border-success-700 tw-rounded-2.5xl tw-text-success-700 tw-font-bold tw-text-xs tw-uppercase"
                >
                  {{ property.gaVersion }}
                </span>
                <AlreadyConnectedTag v-if="connectedSelection?.propertyId === property.id" />
              </div>
            </div>
          </RadioBox>
        </RadioBoxGrid>
        <RadioBoxGrid v-show="!accountProperties.length && !selectedAccountId">
          <PlaceholderRadioBox />
          <PlaceholderRadioBox />
        </RadioBoxGrid>
        <p v-show="!accountProperties.length && !!selectedAccountId">
          No properties found, please choose another account.
        </p>

        <div class="tw-flex tw-flex-col-reverse sm:tw-flex-row tw-gap-2 tw-mt-8">
          <DefaultButton
            as="a"
            href="/google/oauth/cancel"
            class="sm:tw-mr-auto"
            variant="base"
          >
            Cancel
          </DefaultButton>
          <DefaultButton
            class="sm:tw-ml-auto"
            :disabled="!selectedAccountId || !selectedPropertyId || isPending || isSuccess"
            :loading="isPending"
            @click="submit"
          >
            Connect
          </DefaultButton>
        </div>
      </template>
    </StepBox>
  </div>
</template>
